import React, {
  useEffect,
  useState,
  forwardRef,
  ForwardedRef,
  useCallback,
  useRef,
} from 'react';

import Img from '@wconcept/wconcept-ui-renewal/components/atoms/Img';
import Tag from '@wconcept/wconcept-ui-renewal/components/atoms/Tag';
import {
  IconHeart03,
  IconStar01,
  IconArrowRight05,
  IconWOnly01,
} from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import LikeBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/LikeBtn';
import ViewStylVdBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/ViewStylVdBtn';
import PlayIcon from '@wconcept/wconcept-ui-renewal/components/molecules/_icon/PlayIcon';
import VeVideo from '@wconcept/wconcept-ui-renewal/components/atoms/VeVideo';
import { IPlayAuthTokenConfig } from '@byteplus/veplayer';
import {
  classNameBind,
  setComma,
} from '@wconcept/wconcept-common/utils/commonUtils';
import {
  isUndefined,
  isEmpty,
  isEmptyArray,
} from '@wconcept/wconcept-common/utils/baseUtils';
import { getResizeImageUrl } from '@wconcept/wconcept-common/utils/domainUtil';

import * as S from './style';

const MULTI_COLOR_CHIP_IMAGE_URL =
  'https://static.wconcept.co.kr/mobile/images/search_new/img_colorchip_Multi.png';

const getColorChipValue = (value: string) =>
  value.trim().toLowerCase() === 'multi' ? MULTI_COLOR_CHIP_IMAGE_URL : value;

interface IProps {
  custNo?: string;
  itemCd?: string | undefined;
  statusCd?: string;
  itemTypeCd?: number | undefined;
  finalPrice?: number;
  customerPrice?: number;
  finalDiscountRate?: number;
  reviewScore?: number;
  reviewCnt?: number;
  heartCnt?: number;
  /** true면 하트 카운트 UI를 노출·갱신하지 않음 (3열 LP 등) */
  hideHeartCnt?: boolean;
  reserveTag?: string | null;
  saleTag?: string | null;
  infoTags?: string[];
  imageUrl?: string;
  imageAlt?: string;
  rs?: string;
  exclusive?: boolean;
  brandCd?: number;
  brandNameEn?: string;
  brandNameKr?: string;
  itemNameSub?: string;
  itemName?: string;
  myHeartItem?: boolean;
  todayDeliveryTag?: boolean;
  priority?: boolean;
  onClickHeart?: (
    myHeartItemCd: string,
    active: boolean,
    heartCnt: number
  ) => Promise<string | void | null | undefined>;
  doAfterAddMyHeart?: () => void;
  doAfterDeleteMyHeart?: () => void;
  eventBeginDate?: string;
  eventEndDate?: string;
  isLogin?: boolean;
  isDesktop?: boolean;
  isAd?: boolean;
  rank?: string | number;
  className?: string;
  onClick?: ({ heartCnt }: { heartCnt: number }) => void;
  type?: 'all' | 'simple' | 'list' | 'simple-small' | 'simple-lg' | undefined;
  colorType?: 'white' | undefined | string;
  viewStylVdUrl?: string;
  onClickViewStylVd?: () => void;
  videoSrc?: string;
  vid?: string;
  token?: IPlayAuthTokenConfig;
  getNewToken?: () => Promise<IPlayAuthTokenConfig>;
  isActive?: boolean;
  itemRef?: React.Ref<HTMLDivElement>;
  onVideoEnd?: () => void;
  colors?: string[];
}

const PrdcUnitItem = forwardRef(
  (
    {
      custNo,
      itemCd,
      priority,
      statusCd,
      itemTypeCd,
      finalPrice,
      customerPrice,
      finalDiscountRate,
      reviewScore,
      reviewCnt,
      heartCnt,
      hideHeartCnt = false,
      reserveTag,
      saleTag,
      infoTags,
      imageUrl,
      imageAlt,
      rs,
      exclusive,
      brandCd,
      brandNameEn,
      brandNameKr,
      itemNameSub,
      itemName,
      myHeartItem,
      todayDeliveryTag,
      onClickHeart,
      doAfterAddMyHeart,
      doAfterDeleteMyHeart,
      eventBeginDate,
      eventEndDate,
      isLogin,
      isDesktop,
      rank,
      className,
      onClick,
      type = 'all',
      isAd = false,
      colorType,
      viewStylVdUrl,
      onClickViewStylVd,
      videoSrc,
      vid,
      token,
      getNewToken,
      isActive = false,
      itemRef,
      onVideoEnd,
      colors,
    }: IProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const soldOut = statusCd === '02' || statusCd === '04';
    // const isLogin = useRecoilValue(getIsLogin);
    // hideHeartCnt 또는 heartCnt 미전달(3열 LP 등)일 때는 카운트 UI를 노출·갱신하지 않음
    const showHeartCnt = !hideHeartCnt && heartCnt !== undefined;
    const [hrtCnt, setHrtCnt] = useState(heartCnt as number);
    const [isHeart, setIsHeart] = useState(myHeartItem);
    const isProcessing = useRef(false);
    const isHeartRef = useRef(myHeartItem);

    useEffect(() => {
      isHeartRef.current = !!isHeart;
    }, [isHeart]);

    // 비디오 관련
    const [videoEl, setVideoEl] = useState<any>(null);
    const [showVideo, setShowVideo] = useState(false);

    const cName = classNameBind([
      'product-item item',
      type ? `type-${type}` : '',
      colorType ? `color-type-${colorType}` : '',
      className || '',
    ]);

    // 영상 2초간 대기 컨트롤 관련
    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (isActive) {
        setShowVideo(false);
        timer = setTimeout(() => {
          setShowVideo(true);
        }, 2000);
      } else {
        setShowVideo(false);
      }
      return () => {
        clearTimeout(timer);
        setShowVideo(false);
      };
    }, [isActive]);

    // 비디오 음소거 자동재생
    useEffect(() => {
      const videoElement = videoEl?.player;
      if (!videoElement) return;

      if (showVideo) {
        videoElement
          ?.play()
          ?.then(() => {
            if (videoElement.paused) {
              videoElement.play();
            }
          })
          ?.catch((err: Error) => {
            console.error('비디오 재생 에러', err);
          });
      } else {
        videoElement.pause();
        videoElement.currentTime = 0;
      }

      // eslint-disable-next-line consistent-return
      return () => {
        videoElement.pause();
        videoElement.currentTime = 0;
      };
    }, [showVideo, videoEl]);

    useEffect(() => {
      setIsHeart(myHeartItem);
      isHeartRef.current = !!myHeartItem;
    }, [myHeartItem]);

    useEffect(() => {
      setHrtCnt(heartCnt as number);
    }, [heartCnt]);

    //-------------------------------------------------------
    // 상품유닛개선
    //-------------------------------------------------------

    // ITEM RENDERER : getRankRender(랭킹 마크)
    const getRankRender = () => (
      <>{rank && <S.Rank className="rank">{rank}</S.Rank>}</>
    );

    // ITEM RENDERER : getImgRender(상품 이미지)
    const getImgRender = () => (
      <S.Img ref={itemRef}>
        <Img
          priority={priority}
          src={getResizeImageUrl(
            imageUrl,
            // eslint-disable-next-line no-nested-ternary
            isEmpty(rs) ? (isDesktop ? '412' : '374') : rs
          )}
          alt={imageAlt}
        />
        {exclusive && type !== 'list' && (
          <div className="emblem-area">
            <IconWOnly01 />
          </div>
        )}
      </S.Img>
    );

    // ITEM RENDERER : getVideoRender(상품 영상)
    const getVideoRender = () => (
      <>
        {(videoSrc || vid) && !isDesktop && (
          <>
            <VeVideo
              active={showVideo}
              src={videoSrc}
              muted
              vid={vid}
              getNewToken={getNewToken}
              token={token}
              handleComplete={el => {
                if (el) setVideoEl(el);
              }}
              handleEnded={() => {
                setShowVideo(false);
                onVideoEnd?.();
              }}
            />
            <PlayIcon size="sm" />
          </>
        )}
      </>
    );

    // ITEM RENDERER : getTitleRender(상품 타이틀)
    const getTitleRender = () => (
      <S.Title className="prdc-title">
        {brandNameEn && <Text className="title">{brandNameEn}</Text>}
        {brandNameKr && <Text className="title">{brandNameKr}</Text>}
        {type === 'all' && itemNameSub && itemName ? (
          <Text className="detail">
            {itemNameSub} {itemName}
          </Text>
        ) : (
          <>
            {type === 'all' && itemNameSub && (
              <Text className="front">{itemNameSub}</Text>
            )}
            {itemName && <Text className="detail">{itemName}</Text>}
          </>
        )}
      </S.Title>
    );

    // ITEM RENDERER : getPriceRender(상품 가격)
    const getPriceRender = () => {
      let isCustomerPrice = false;
      let isFinalDiscountRate = false;

      if (finalPrice !== customerPrice && customerPrice !== undefined) {
        isCustomerPrice = true;
      }

      if (!!finalDiscountRate && finalDiscountRate > 0) {
        isFinalDiscountRate = true;
      }

      return (
        <S.Price className="prdc-price">
          {isCustomerPrice && (
            <Text className="customer-price">
              {setComma(String(customerPrice))}
            </Text>
          )}
          {isFinalDiscountRate ? (
            <Text em className="final-discount">
              {finalDiscountRate}
              <span>%</span>
            </Text>
          ) : null}
          <Text strong className="final-price">
            {setComma(String(finalPrice))}
          </Text>
        </S.Price>
      );
    };

    // ITEM RENDERER : getStatsRender(리뷰/찜하기 정보)
    const getStatsRender = () => {
      const getCnt = (v: string) => <Text className="cnt">{v}</Text>;

      return (
        <>
          {((!!reviewCnt && reviewCnt > 0) ||
            (showHeartCnt && !!hrtCnt && hrtCnt > 0)) && (
            <S.Stats className="stats">
              {!!reviewCnt && reviewCnt > 0 && (
                <span className="review">
                  <IconStar01 />
                  <em className="score">
                    {reviewScore ? reviewScore.toFixed(1) : ''}
                  </em>
                  {reviewCnt > 9999
                    ? getCnt(`(9,999+)`)
                    : getCnt(`(${setComma(String(reviewCnt))})`)}
                </span>
              )}
              {showHeartCnt && !!hrtCnt && hrtCnt > 0 && (
                <span className="like">
                  <IconHeart03 />
                  {hrtCnt > 9999
                    ? getCnt(`9,999+`)
                    : getCnt(`${setComma(String(hrtCnt))}`)}
                </span>
              )}
            </S.Stats>
          )}
        </>
      );
    };

    // ITEM RENDERER : getLikeRender(찜하기 버튼)
    const getLikeRender = useCallback(
      () => (
        <>
          {!isUndefined(myHeartItem) && (
            <LikeBtn
              active={!!isHeart}
              heartColor={colorType}
              size={isDesktop ? 'lg' : 'md'}
              onClick={async (
                e: React.MouseEvent<HTMLButtonElement, MouseEvent>
              ) => {
                e.stopPropagation();
                if (isProcessing.current) return;
                isProcessing.current = true;

                const currentHeart = isHeartRef.current;

                try {
                  const res = await onClickHeart?.(
                    itemCd || '',
                    !!currentHeart,
                    hrtCnt || 0
                  );
                  if (!isLogin) return;
                  if (res === 'SUCCESS') {
                    isHeartRef.current = !currentHeart;
                    setIsHeart(prev => !prev);
                    if (showHeartCnt) {
                      setHrtCnt(prev => {
                        if (currentHeart) {
                          return prev ? prev - 1 : prev;
                        }
                        return (prev || 0) + 1;
                      });
                    }
                  }
                } catch (error: unknown) {
                  console.error('찜하기 실패', error);
                } finally {
                  isProcessing.current = false;
                }
              }}
            />
          )}
        </>
      ),
      [
        colorType,
        hrtCnt,
        isDesktop,
        isHeart,
        isLogin,
        itemCd,
        myHeartItem,
        onClickHeart,
        showHeartCnt,
      ]
    );

    // ITEM RENDERER : getSoldoutTagRender(품절 태그)
    const getSoldoutTagRender = () => (
      <S.AreaTag className="area-tag">
        <Tag className="tag-soldout">품절</Tag>
      </S.AreaTag>
    );

    // ITEM RENDERER : getSaleTagRender
    const getSaleTagRender = () => {
      if (saleTag) {
        return <>{saleTag && <Tag className="tag-sale">{saleTag}</Tag>}</>;
      }
      return null;
    };

    // ITEM RENDERER : getDeliveryTagRender
    const getDeliveryTagRender = () => {
      if (todayDeliveryTag) {
        return <Tag>오늘출발</Tag>;
      }
      return null;
    };

    // ITEM RENDERER : getAddTagRender(광고 태그)
    const getAddTagRender = () => (
      <S.AreaAdTag className="area-ad-tag">
        <Tag className="tag-ad">광고</Tag>
      </S.AreaAdTag>
    );

    // ITEM RENDERER : getInfoTagsRender
    const getInfoTagsRender = () => {
      if (
        (infoTags !== undefined && !isEmptyArray(infoTags)) ||
        todayDeliveryTag
      ) {
        return (
          <S.AreaTag className="area-tag">
            <>
              {infoTags?.map((info: any, index: number) => (
                // eslint-disable-next-line react/no-array-index-key
                <Tag key={index}>{info}</Tag>
              ))}
              {getDeliveryTagRender()}
            </>
          </S.AreaTag>
        );
      }
      return null;
    };

    const handleClick = useCallback(() => {
      onClick?.({
        heartCnt: hrtCnt,
      });
    }, [hrtCnt, onClick]);

    // ITEM RENDERER : 리스트형 타입
    const getTypeListRender = () => (
      <>
        {getRankRender()}
        <S.AreaClick className="area-img" onClick={handleClick}>
          {getImgRender()}
        </S.AreaClick>
        <S.AreaInfo className="area-info">
          <S.AreaClick className="area-click" onClick={handleClick}>
            {getTitleRender()}
            {getPriceRender()}
            {soldOut && getSoldoutTagRender()}
          </S.AreaClick>
          {getLikeRender()}
        </S.AreaInfo>
      </>
    );

    // ITEM RENDERER : 기본형 타입
    const getTypeSimpleRender = () => (
      <>
        <S.AreaClick className="area-img" onClick={handleClick}>
          {getImgRender()}
          {isAd && !isDesktop ? getAddTagRender() : null}
        </S.AreaClick>
        <S.AreaInfo className="area-info">
          <S.AreaClick className="area-click" onClick={handleClick}>
            {getTitleRender()}
            {getPriceRender()}
            {soldOut && getSoldoutTagRender()}
          </S.AreaClick>
          {getLikeRender()}
        </S.AreaInfo>
        {getRankRender()}
      </>
    );

    // ITEM RENDERER : 컬러칩
    const getColorChipsRender = () => {
      if (!colors || colors.length === 0) return null;
      const visible = colors.slice(0, 5);
      const more = colors.length - 5;

      const isImageChip = (value: string) =>
        /^(https?:\/\/|data:image\/)/i.test(value);

      return (
        <S.ColorChips className="color-chips">
          {visible.map((value, i) => {
            const chipValue = getColorChipValue(value);
            const imageChip = isImageChip(chipValue);

            return (
              <span
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={`chip${imageChip ? ' is-image' : ''}`}
                style={
                  imageChip
                    ? {
                        backgroundImage: `url(${chipValue})`,
                        backgroundColor: 'transparent',
                      }
                    : { backgroundColor: chipValue }
                }
              />
            );
          })}
          {more > 0 && <span className="chip-more">+{more}</span>}
        </S.ColorChips>
      );
    };

    // ITEM RENDERER : all 타입
    const getTypeAllRender = () => (
      <>
        <S.AreaClick className="area-img" onClick={handleClick}>
          {getImgRender()}
          {getVideoRender()}
          {isAd && !isDesktop ? getAddTagRender() : null}
        </S.AreaClick>
        <S.AreaInfo className="area-info">
          <S.AreaClick className="area-click" onClick={handleClick}>
            {getTitleRender()}
            {getPriceRender()}
            {getStatsRender()}
            {!soldOut && getSaleTagRender()}
            {soldOut ? getSoldoutTagRender() : getInfoTagsRender()}
          </S.AreaClick>
          {viewStylVdUrl && <ViewStylVdBtn onClick={onClickViewStylVd} />}
          {getLikeRender()}
          {getColorChipsRender()}
        </S.AreaInfo>
        {getRankRender()}
      </>
    );

    return (
      <S.PrdcUnitItem className={cName} ref={ref}>
        {type === 'all' && getTypeAllRender()}
        {type === 'simple' && getTypeSimpleRender()}
        {type === 'simple-small' && getTypeSimpleRender()}
        {type === 'simple-lg' && getTypeSimpleRender()}
        {/* simple과 simple-* 구성은 같으나 스타일이 다름 */}
        {type === 'list' && getTypeListRender()}
      </S.PrdcUnitItem>
    );
  }
);

PrdcUnitItem.displayName = 'PrdcUnitItem';

export default PrdcUnitItem;
